import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { AlertTriangle, Share2, Printer, X, ArrowRight, ChevronDown, Check } from "lucide-react";
import { motion } from "framer-motion";
import { Skeleton } from "@/components/ui/skeleton";

const API = process.env.REACT_APP_BACKEND_URL || "https://propquant-api.onrender.com";

// ── Design tokens — the only place a colour literal may live ──────
const T = {
  bgPage: "#F5F3EF",
  card: "#FFFFFF",
  cardBorder: "rgba(0,189,204,0.22)",
  hairline: "rgba(15,14,12,0.08)",
  ink: "#0F0E0C",
  body: "#6A6560",
  bodyStrong: "#2E2B28",
  muted: "#9A958F",
  dark: "#080d16",
  darkText: "#f1f5f9",
  darkBody: "#e2e8f0",
  accent: "#00E5FF",
  accentInk: "#007A8A",
  accentSoft: "rgba(0,229,255,0.08)",
  good: "#157F4A",
  goodSoft: "rgba(21,127,74,0.10)",
  warn: "#A96C11",
  warnSoft: "rgba(169,108,17,0.12)",
  bad: "#B4321F",
  badSoft: "rgba(180,50,31,0.10)",
  // Derived tokens: translucent forms of the colours above
  darkFade: "rgba(8,13,22,0.70)",
  darkClear: "rgba(8,13,22,0)",
  accentGlow: "rgba(0,229,255,0.12)",
  accentClear: "rgba(0,229,255,0)",
  shadow: "rgba(15,14,12,0.07)",
  shadowLg: "rgba(8,13,22,0.12)",
  boxSoft: "rgba(8,13,22,0.04)",
  white80: "rgba(255,255,255,0.8)",
  onDarkFaint: "rgba(241,245,249,0.06)",
  onDarkSoft: "rgba(241,245,249,0.16)",
  onDarkBorder: "rgba(241,245,249,0.2)",
  glass: "rgba(255,255,255,0.9)",
  white: "#FFFFFF",
  badBorder30: "rgba(180,50,31,0.3)",
  warnBorder30: "rgba(169,108,17,0.3)",
  goodBorder30: "rgba(21,127,74,0.3)",
};

// Readable labels for checklist items — fallback only; the API's own
// item.label (and cat.title below) always wins when present.
const ITEM_LABELS = {
  doors: { "1": "Opens and closes smoothly", "2": "Self-latches correctly", "3": "Frame straight, no gaps", "4": "Frame finish complete", "5": "No wobble when pushed", "6": "Lock and handle work", "7": "No rust on hardware", "8": "Stopper present and fixed", "9": "No surface damage", "10": "Strike plate flush" },
  windows: { "1": "Glass intact", "2": "Opens, slides, and locks", "3": "No abnormal noise", "4": "Silicone seal present", "5": "Frame properly aligned", "6": "Gasket rubber intact", "7": "Mosquito net intact", "8": "No scratches" },
  flooring: { "1": "No hollow tiles", "2": "No cracked or chipped tiles", "3": "Tiles level", "4": "Grout filled evenly", "5": "Skirting joint filled", "6": "Floor slopes to drain" },
  walls: { "1": "No wall cracks", "2": "No water stains or damp", "3": "No plaster damage", "4": "No construction material left", "5": "Corners and edges intact" },
  painting: { "1": "No hairline cracks", "2": "Paint finish even", "3": "No unpainted gaps", "4": "No peeling or flaking" },
  dado: { "1": "No hollow dado tiles", "2": "No cracked dado tiles", "3": "No gap at counter joint", "4": "No gap at tap cutouts", "5": "Tiles level and aligned" },
  woodwork: { "1": "Shutters open and close smoothly", "2": "Laminate intact", "3": "No frame cracks" },
  granite: { "1": "No cracks in granite or marble", "2": "No debonding", "3": "Edges flush with wall" },
  plumbing: { "1": "Flush works", "2": "No visible leaks", "3": "Shower properly fixed", "4": "No rust on fittings", "5": "WC seat fixed", "6": "No pests near drains" },
};

const CAT_TITLES = {
  doors: "Doors", windows: "Windows", flooring: "Flooring", walls: "Walls and ceiling",
  painting: "Painting", dado: "Dado tiles", woodwork: "Woodwork and cabinets",
  granite: "Granite and marble", plumbing: "Plumbing",
};

// ── Lookups ───────────────────────────────────────────────────────
const RESP = {
  seller_fix: {
    label: "Seller must fix",
    colour: T.bad,
    text: "Seller must fix before registration. Get it repaired at their cost and re-inspected. Once the sale deed is signed, it's yours.",
  },
  negotiate: {
    label: "Negotiate with seller",
    colour: T.warn,
    text: "Use as price leverage. A real defect you can live with; ask for a reduction rather than a repair.",
  },
  post_movein: {
    label: "Fix after moving in",
    colour: T.good,
    text: "Fix after moving in. Cosmetic and low-cost; not worth spending negotiation goodwill on.",
  },
};

const VERDICT_COLOUR = { bad: T.bad, warn: T.warn, good: T.good };

const SEVERITY_RANK = { high: 0, moderate: 1, low: 2 };
const RESP_RANK = { seller_fix: 0, negotiate: 1, post_movein: 2 };
const TONE_RANK = { bad: 0, warn: 1, good: 2 };

const TONE_COLOURS = {
  bad: { fg: T.bad, bg: T.badSoft, border: T.badBorder30 },
  warn: { fg: T.warn, bg: T.warnSoft, border: T.warnBorder30 },
  good: { fg: T.good, bg: T.goodSoft, border: T.goodBorder30 },
};

const severityTone = (severity) =>
  severity === "high"
    ? { label: "Action required", ...TONE_COLOURS.bad }
    : { label: "Monitor", ...TONE_COLOURS.warn };

// Severity isn't persisted on every historical row yet (fixed going forward —
// see InspectorForm.jsx submission + report_routes.py). Until every row has
// it, derive a safe default: seller_fix implies a structural issue, so treat
// it as high even when no severity was recorded.
const sevOf = (item) =>
  item.severity === "high" ? "high"
  : item.responsibility === "seller_fix" ? "high"
  : "moderate";

const fmtDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "—";

// Display-only formatting — the address/name values themselves (source of
// truth, used in the API payload and anywhere else read back) are never
// rewritten. Capitalises the first letter of each word only, so numbers
// ("201") and any capitals already in the source ("AR", "McDonald") are
// left alone.
const titleCase = (s) =>
  (s || "")
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = () => setMatches(mq.matches);
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

function usePrefersReducedMotion() {
  return useMediaQuery("(prefers-reduced-motion: reduce)");
}

export default function InspectionReport() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openCats, setOpenCats] = useState({});
  const [lightbox, setLightbox] = useState(null); // { title, photos: [{label, src}] }
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/report/${reportId}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Report not found");
        }
        setReport(await res.json());
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [reportId]);

  // Lightbox: Esc closes, body scroll locked while open
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightbox]);

  const toggle = (k) => setOpenCats((p) => ({ ...p, [k]: !p[k] }));

  const goToCat = useCallback((catId) => {
    setOpenCats((p) => ({ ...p, [catId]: true }));
    requestAnimationFrame(() => {
      document.getElementById(`cat-${catId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  const r = report;

  // Severity first, then who handles it.
  const failedItems = useMemo(() => {
    const items = [...(r?.failed_items || [])];
    items.sort((a, b) =>
      ((SEVERITY_RANK[sevOf(a)] ?? 1) - (SEVERITY_RANK[sevOf(b)] ?? 1)) ||
      ((RESP_RANK[a.responsibility] ?? 1) - (RESP_RANK[b.responsibility] ?? 1))
    );
    return items;
  }, [r]);

  // System summary tiles: Action required, then Monitor, then Satisfactory.
  const tiles = useMemo(() => {
    const categories = r?.categories || [];
    return categories
      .map((cat) => {
        const catFails = failedItems.filter((f) => f.category === cat.id || f.category_title === cat.title);
        const toneKey = cat.failed === 0 ? "good" : catFails.some((f) => sevOf(f) === "high") ? "bad" : "warn";
        return { cat, toneKey };
      })
      .sort((a, b) => TONE_RANK[a.toneKey] - TONE_RANK[b.toneKey]);
  }, [r, failedItems]);

  const recs = useMemo(() => r?.recommendations || {}, [r]);
  const steps = useMemo(() => {
    const out = [];
    if (recs?.seller_fix?.count > 0) {
      out.push({
        key: "seller_fix", num: "1", colour: T.bad,
        title: "Demand the seller fix these before registration",
        desc: `${recs.seller_fix.count} structural finding${recs.seller_fix.count > 1 ? "s" : ""}. Get them repaired at the seller's cost and re-inspected before you register — once the sale deed is signed, they are yours.`,
        items: recs.seller_fix.items,
      });
    }
    if (recs?.negotiate?.count > 0) {
      out.push({
        key: "negotiate", num: "2", colour: T.warn,
        title: `Use these ${recs.negotiate.count} as price leverage`,
        desc: "Real defects, but ones you can live with and repair on your own schedule. Bring them to the table together, with photos, and ask for a reduction rather than a repair.",
        items: recs.negotiate.items,
      });
    }
    if (recs?.post_movein?.count > 0) {
      out.push({
        key: "post_movein", num: "3", colour: T.good,
        title: "Handle these after you move in",
        desc: "Cosmetic and low-cost. Not worth spending negotiation goodwill on — fold them into your own touch-up work.",
        items: recs.post_movein.items,
      });
    }
    return out;
  }, [recs]);

  const handleShare = () => {
    const text = `Inspection report for ${r?.address ? titleCase(r.address) : "the property"}\n${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const shareActionList = () => {
    const lines = [];
    const groups = [
      ["Seller must fix", recs?.seller_fix],
      ["Negotiate", recs?.negotiate],
      ["Fix after moving in", recs?.post_movein],
    ];
    groups.forEach(([label, group]) => {
      if (!group?.count) return;
      if (lines.length) lines.push("");
      lines.push(`${label}:`);
      (group.items || []).forEach((it) => lines.push(`- ${it.label}${it.location ? ` (${it.location})` : ""}`));
    });
    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  // ── Loading — skeletons, never a spinner ───────────────────────
  if (loading) {
    return (
      <div className="ir-page" style={S.page}>
        <div className="ir-hero" style={{ ...S.hero, display: "block" }}>
          <div className="ir-container">
            <div className="ir-hero-inner" style={S.heroInner}>
              <SkelBar w={160} h={22} dark />
              <div>
                <SkelBar w={190} h={11} dark />
                <div style={{ height: 14 }} />
                <SkelBar w="86%" h={42} dark />
              </div>
            </div>
          </div>
        </div>

        <div className="ir-container">
          <div className="ir-stats-strip" style={{ ...S.statsStrip, display: "flex" }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="ir-stat" style={{ padding: "18px 16px", flex: 1 }}>
                <SkelBar w={62} h={10} />
                <div style={{ height: 11 }} />
                <SkelBar w="70%" h={18} />
              </div>
            ))}
          </div>

          <div className="ir-sect" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {[0, 1].map((i) => (
              <div key={i} className="ir-finding ir-has-photo" style={{ ...S.cardBase, overflow: "hidden" }}>
                <div className="ir-fig"><SkelBar w="100%" h="100%" radius={0} /></div>
                <div className="ir-ftext" style={S.findingText}>
                  <SkelBar w={130} h={10} />
                  <div style={{ height: 14 }} />
                  <SkelBar w="80%" h={22} />
                  <div style={{ height: 14 }} />
                  <SkelBar w="100%" h={12} />
                  <div style={{ height: 8 }} />
                  <SkelBar w="92%" h={12} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <style>{CSS}</style>
      </div>
    );
  }

  // ── Error — always with a next action ──────────────────────────
  if (error) {
    const pending = error === "Report not available yet";
    return (
      <div className="ir-page" style={S.page}>
        <div className="ir-container">
          <div style={{ padding: "80px 0", textAlign: "center" }}>
            <AlertTriangle size={28} color={T.warn} style={{ marginBottom: 16 }} />
            <h2 style={{ ...S.h, fontSize: 22, marginBottom: 8 }}>
              {pending ? "Report in progress" : "Report not found"}
            </h2>
            <p style={{ fontSize: 14, color: T.body, lineHeight: 1.6, maxWidth: 340, margin: "0 auto 26px" }}>
              {pending
                ? "Our team is reviewing this inspection. You'll receive the link on WhatsApp once it's ready."
                : "This link may be invalid or expired. Contact Propspector for help."}
            </p>
            {pending ? (
              <a
                href="https://wa.me/?text=I%20am%20waiting%20on%20my%20Propspector%20inspection%20report"
                target="_blank" rel="noreferrer"
                className="ir-btn" style={S.btnPrimary}
              >
                <Share2 size={16} /> Message us on WhatsApp
              </a>
            ) : (
              <Link to="/inspect" className="ir-btn" style={S.btnPrimary}>
                Book an inspection <ArrowRight size={16} />
              </Link>
            )}
          </div>
        </div>
        <style>{CSS}</style>
      </div>
    );
  }

  const categories = r.categories || [];
  const passed = r.passed || 0;
  const failed = r.failed || 0;
  const checked = passed + failed;
  const score = checked > 0 ? Math.round((passed / checked) * 100) : null;
  const scoreColour = score === null ? T.muted : score >= 85 ? T.good : score >= 65 ? T.warn : T.bad;
  const verdictColour = VERDICT_COLOUR[r.verdict_type] || T.ink;
  const ref = r.report_id?.slice(0, 8).toUpperCase() || "—";
  const title = r.address ? titleCase(r.address) : "Property Inspection";
  const inspectorName = r.inspector_name ? titleCase(r.inspector_name) : "";
  const sellerFixCount = failedItems.filter((f) => f.responsibility === "seller_fix").length;

  const contextLine =
    checked === 0 ? "Nothing recorded yet"
    : failed === 0 ? `All ${checked} checks passed`
    : sellerFixCount > 0 ? `${failed} of ${checked} checks need attention · ${sellerFixCount} must be fixed by the seller`
    : `${failed} of ${checked} checks need attention`;

  // Zig-zag alternation counts photo-findings only, so a text-only
  // finding never breaks the rhythm.
  let photoIndex = -1;

  return (
    <div className="ir-page" style={S.page}>

      {/* ── Lightbox ───────────────────────────────────────────── */}
      {lightbox && (
        <div style={{ position: "fixed", inset: 0, zIndex: 40, background: T.dark, overflowY: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "14px 16px", position: "sticky", top: 0, background: T.dark }}>
            <span style={{ ...S.label, fontSize: 10, color: T.darkBody, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {lightbox.title}
            </span>
            <button
              onClick={() => setLightbox(null)} aria-label="Close photos" className="ir-btn"
              style={{ width: 44, height: 44, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", cursor: "pointer", color: T.darkText }}
            >
              <X size={20} />
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 20, padding: "4px 16px 40px", maxWidth: 720, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
            {lightbox.photos.map((photo, i) => (
              <div key={i}>
                <img src={photo.src} alt={photo.label} style={{ width: "100%", borderRadius: 10, display: "block" }} />
                <div style={{ display: "flex", gap: 8, alignItems: "baseline", paddingTop: 9 }}>
                  <span style={{ ...S.label, fontSize: 10, color: T.accent }}>{String(i + 1).padStart(2, "0")}</span>
                  <span style={{ fontSize: 13, lineHeight: 1.45, color: T.darkBody }}>{photo.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── HERO (full-bleed) ────────────────────────────────────── */}
      <header className={`ir-hero${r.cover_photo ? "" : " ir-hero--nophoto"}`} style={S.hero}>
        {r.cover_photo ? (
          <>
            <img src={r.cover_photo} alt="" style={S.heroImg} />
            <div style={S.heroScrim} />
          </>
        ) : (
          <div className="ir-hero-glow" style={S.heroGlow} />
        )}

        <div className="ir-container ir-hero-container">
          <div className="ir-hero-inner" style={S.heroInner}>
            <div className="ir-hero-topbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <Link to="/" className="ir-brand" style={{ display: "flex", alignItems: "center", gap: 9, textDecoration: "none" }}>
                <img src="/logo.png" alt="" style={{ width: 22, height: 22, borderRadius: 6 }} />
                <span className="ir-brand-name" style={{ ...S.h, fontSize: 15, color: T.darkText }}>Propspector</span>
              </Link>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="ir-ref" style={S.refPill}>{ref}</span>
                <button onClick={() => window.print()} className="ir-btn no-print" style={S.exportPill}>
                  <Printer size={14} /> Export PDF
                </button>
              </div>
            </div>

            <div>
              <div className="ir-hero-eyebrow" style={{ ...S.label, fontSize: 13, letterSpacing: "0.25em", color: T.white80, marginBottom: 14 }}>
                PROPERTY INSPECTION REPORT
              </div>
              <h1 className="ir-hero-title" style={{ ...S.h, fontSize: "clamp(38px, 6vw, 64px)", lineHeight: 1.05, color: T.darkText }}>
                {title}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <div className="ir-container">

        {/* ── STATS STRIP (overlaps hero by -48px) ───────────────── */}
        <div className="ir-stats-strip" style={S.statsStrip}>
          <StatCell label="Inspection date" value={fmtDate(r.inspection_date || r.created_at)} />
          <StatCell label="Inspector" value={inspectorName || "—"} />
          <StatCell label="Checkpoints" value={`${checked}`} />
          <StatCell label="Overall score" value={score === null ? "—" : `${score} / 100`} valueColour={scoreColour} />
        </div>

        {/* ── VERDICT (plain section) ─────────────────────────────── */}
        <section className="ir-sect ir-verdict-sect" style={S.sect}>
          <div className="ir-verdict-line" style={{ ...S.h, fontSize: "clamp(22px, 3vw, 28px)", color: verdictColour }}>
            {r.verdict}
          </div>
          <p className="ir-verdict-context" style={{ fontSize: 15, color: T.muted, margin: "10px 0 20px" }}>{contextLine}</p>
          <p className="ir-summary" style={{ fontSize: 16, lineHeight: 1.7, color: T.bodyStrong, maxWidth: "68ch", margin: "0 0 24px" }}>
            {r.summary || `${passed} checkpoints passed and ${failed} issues were identified during this inspection.`}
          </p>
          <div style={{ maxWidth: 480 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.muted, marginBottom: 6, fontVariantNumeric: "tabular-nums" }}>
              <span>{passed} passed</span>
              <span>{failed} issue{failed !== 1 ? "s" : ""}</span>
            </div>
            <div style={{ display: "flex", height: 6, borderRadius: 50, overflow: "hidden", gap: 2, background: T.hairline }}>
              <div style={{ flex: passed || 1, background: T.good }} />
              {failed > 0 && <div style={{ flex: failed, background: T.bad }} />}
            </div>
          </div>
        </section>

        {/* ── SYSTEM SUMMARY ───────────────────────────────────── */}
        {categories.length > 0 && (
          <section className="ir-sect" style={S.sect}>
            <h2 className="ir-sect-h" style={{ ...S.h, fontSize: "clamp(24px, 3vw, 30px)", margin: "0 0 20px" }}>
              System summary
            </h2>

            <div className="ir-tiles">
              {tiles.map(({ cat, toneKey }) => {
                const tone = TONE_COLOURS[toneKey];
                const label = { bad: "Action required", warn: "Monitor", good: "Satisfactory" }[toneKey];

                return (
                  <button
                    key={cat.id}
                    onClick={() => goToCat(cat.id)}
                    className="ir-tile"
                  >
                    <div className="ir-tile-title">
                      {cat.title || CAT_TITLES[cat.id] || cat.id}
                    </div>
                    <Badge tone={{ label, ...tone }} fullWidth />
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* ── DETAILED FINDINGS ────────────────────────────────── */}
        {failedItems.length > 0 && (
          <section className="ir-sect" style={S.sect}>
            <h2 className="ir-sect-h" style={{ ...S.h, fontSize: "clamp(24px, 3vw, 30px)", margin: "0 0 10px" }}>
              Detailed findings
            </h2>
            <p style={{ fontSize: 15, lineHeight: 1.55, color: T.muted, margin: "0 0 28px", maxWidth: "58ch" }}>
              {failedItems.length} item{failedItems.length !== 1 ? "s" : ""} need attention. Photos taken on site.
            </p>

            <div className="ir-findings-list">
              {failedItems.map((item, i) => {
                const photos = item.photos || [];
                const hasPhotos = photos.length > 0;
                if (hasPhotos) photoIndex += 1;
                const flip = hasPhotos && photoIndex % 2 === 1;
                const tone = severityTone(sevOf(item));
                const resp = RESP[item.responsibility] || RESP.negotiate;
                const num = String(i + 1).padStart(2, "0");
                const hasCost = item.cost_low != null && item.cost_high != null;
                const openLightbox = () =>
                  setLightbox({
                    title: `F-${num} · ${item.label}`,
                    photos: photos.map((src, pi) => ({ src, label: `Photo ${pi + 1} of ${photos.length}` })),
                  });

                return (
                  <motion.article
                    key={`${item.category}-${item.label}-${i}`}
                    className={`ir-finding${hasPhotos ? " ir-has-photo" : ""}${flip ? " ir-finding--flip" : ""}`}
                    initial={reducedMotion ? false : { opacity: 0, y: 16 }}
                    whileInView={reducedMotion ? undefined : { opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.4, delay: Math.min(i, 6) * 0.04 }}
                  >
                    {hasPhotos && (
                      <div className="ir-fig" onClick={openLightbox}>
                        <img src={photos[0]} alt={item.label} loading="lazy" decoding="async" />
                        <span className="ir-photo-pill" style={S.photoPill}>Photo F-{num}</span>
                        {photos.length > 1 && (
                          <span
                            className="ir-photo-more no-print"
                            onClick={(e) => { e.stopPropagation(); openLightbox(); }}
                            style={S.photoMore}
                          >
                            +{photos.length - 1} photos
                          </span>
                        )}
                      </div>
                    )}

                    <div className="ir-ftext" style={S.findingText}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 0 }}>
                        <span style={{ fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase", color: T.accentInk, fontWeight: 600 }}>
                          {[item.location, item.category_title].filter(Boolean).join(" · ") || "GENERAL"}
                        </span>
                        <Badge tone={tone} />
                      </div>

                      <h3 className="ir-ftitle" style={{ ...S.h, fontSize: "clamp(20px, 2.4vw, 24px)", lineHeight: 1.25, color: T.ink, margin: "14px 0 12px" }}>
                        {item.label}
                      </h3>

                      {item.notes && (
                        <p className="ir-desc" style={{ fontSize: 16, lineHeight: 1.65, color: T.bodyStrong, margin: "0 0 16px" }}>
                          {item.notes}
                        </p>
                      )}

                      <div className="ir-rec" style={{ background: T.boxSoft, borderRadius: 10, padding: 16 }}>
                        <div style={{ ...S.label, fontSize: 11, color: resp.colour, marginBottom: 6 }}>
                          {(item.responsibility_label || resp.label).toUpperCase()}
                        </div>
                        <p style={{ fontSize: 14, lineHeight: 1.6, color: T.bodyStrong, margin: 0 }}>{resp.text}</p>
                      </div>

                      <div className="ir-ffooter" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, borderTop: `1px solid ${T.hairline}`, paddingTop: 16, marginTop: "auto" }}>
                        {hasCost ? (
                          <>
                            <span style={{ ...S.label, fontSize: 10, color: T.muted }}>ESTIMATED REPAIR</span>
                            <span style={{ ...S.h, fontWeight: 600, fontSize: 13, color: T.ink, fontVariantNumeric: "tabular-nums" }}>
                              ₹{item.cost_low.toLocaleString("en-IN")}–₹{item.cost_high.toLocaleString("en-IN")}
                            </span>
                          </>
                        ) : (
                          <>
                            <span style={{ ...S.label, fontSize: 10, color: T.muted }}>WHO HANDLES IT</span>
                            <span style={{ ...S.h, fontWeight: 600, fontSize: 13, color: resp.colour }}>
                              {item.responsibility_label || resp.label}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </section>
        )}

        {/* ── WHAT TO DO NEXT ──────────────────────────────────── */}
        <section className="ir-sect" style={S.sect}>
          <h2 className="ir-sect-h" style={{ ...S.h, fontSize: "clamp(24px, 3vw, 30px)", margin: "0 0 10px" }}>What to do next</h2>
          <p style={{ fontSize: 15, color: T.muted, margin: "0 0 28px" }}>Three steps, in order. Send this section to the seller.</p>

          {steps.length > 0 ? (
            <>
              <div className="ir-timeline">
                {steps.map((s, idx) => (
                  <RecStep key={s.key} {...s} isLast={idx === steps.length - 1} />
                ))}
              </div>

              {recs?.seller_fix?.count > 0 && (
                <div className="ir-callout" style={{ border: `1px solid ${T.hairline}`, borderRadius: 12, padding: "16px 18px", marginTop: 24 }}>
                  <p style={{ fontSize: 14, lineHeight: 1.5, color: T.bodyStrong, margin: "0 0 10px" }}>
                    Send this section to the seller before you sign anything.
                  </p>
                  <a
                    href="#" onClick={(e) => { e.preventDefault(); shareActionList(); }}
                    className="ir-btn no-print"
                    style={{ ...S.h, fontSize: 14, fontWeight: 600, color: T.accentInk, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    Share these steps on WhatsApp <ArrowRight size={13} />
                  </a>
                </div>
              )}
            </>
          ) : (
            <div style={{ ...S.cardBase, borderRadius: 16, padding: "32px 24px", textAlign: "center" }}>
              <p style={{ ...S.h, fontWeight: 700, fontSize: 20, color: T.good, margin: "0 0 10px" }}>All clear</p>
              <p style={{ fontSize: 15, color: T.body, lineHeight: 1.6, maxWidth: 400, margin: "0 auto 24px" }}>
                Every checkpoint passed. No issues were found during the physical inspection of this property.
              </p>
              <button onClick={handleShare} className="ir-btn no-print" style={S.btnPrimary}>
                <Share2 size={16} /> Share report with seller
              </button>
            </div>
          )}
        </section>

        {/* ── EVERYTHING WE CHECKED ────────────────────────────── */}
        {categories.length > 0 && (
          <section className="ir-sect" style={S.sect}>
            <h2 className="ir-sect-h" style={{ ...S.h, fontSize: "clamp(24px, 3vw, 30px)", margin: "0 0 10px" }}>Everything we checked</h2>
            <p style={{ fontSize: 15, color: T.muted, margin: "0 0 10px" }}>
              {checked} checkpoints across {categories.length} categor{categories.length !== 1 ? "ies" : "y"}. Tap a category to see each one.
            </p>

            <div className="ir-checklist-card" style={S.cardBase}>
              {categories.map((cat, ci) => {
                const instances = cat.instances || [];
                const expandable = instances.length > 0;
                const isOpen = expandable && !!openCats[cat.id];

                return (
                  <div key={cat.id} id={`cat-${cat.id}`} className="ir-cat" style={{ borderTop: ci === 0 ? "none" : `1px solid ${T.hairline}`, scrollMarginTop: 16 }}>
                    <div
                      className="ir-cat-row"
                      onClick={expandable ? () => toggle(cat.id) : undefined}
                      style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 56, cursor: expandable ? "pointer" : "default", padding: "0 20px" }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ ...S.h, fontWeight: 600, fontSize: 15, color: T.ink, marginBottom: 3 }}>
                          {cat.title || CAT_TITLES[cat.id] || cat.id}
                        </div>
                        <div style={{ fontSize: 12, color: T.muted }}>
                          {instances.length} location{instances.length !== 1 ? "s" : ""} · {cat.passed} passed
                          {cat.failed > 0 && ` · ${cat.failed} failed`}
                        </div>
                      </div>

                      {cat.failed > 0 ? (
                        <span className="ir-fail-pill" style={{ ...S.h, fontWeight: 700, fontSize: 11, color: T.white, background: T.bad, borderRadius: 50, minWidth: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 6px", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                          {cat.failed}
                        </span>
                      ) : (
                        <Check size={16} color={T.good} style={{ flexShrink: 0 }} />
                      )}
                      {expandable && (
                        <ChevronDown
                          size={16} color={T.muted} className="ir-chevron"
                          style={{ flexShrink: 0, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                        />
                      )}
                    </div>

                    {expandable && (
                      <motion.div
                        className="ir-cat-panel"
                        initial={false}
                        animate={{ height: isOpen ? "auto" : 0 }}
                        transition={{ duration: reducedMotion ? 0 : 0.25, ease: "easeInOut" }}
                        style={{ overflow: "hidden" }}
                      >
                        <div style={{ padding: "2px 20px 20px" }}>
                          <div className="ir-print-only" style={{ ...S.label, fontSize: 10, color: T.muted, margin: "0 0 2mm" }}>
                            {cat.passed} PASSED
                          </div>

                          {instances.map((inst, ii) => (
                            <div key={ii}>
                              {inst.location && (
                                <div className="ir-inst-loc" style={{ ...S.label, fontSize: 10, color: T.muted, margin: "12px 0 8px" }}>
                                  {inst.location.toUpperCase()}
                                </div>
                              )}
                              <div style={{ display: "flex", flexDirection: "column" }}>
                                {(inst.items || []).map((item) => {
                                  const label = item.label || ITEM_LABELS[cat.id]?.[item.id] || `Item ${item.id}`;
                                  const row = { display: "flex", gap: 10, fontSize: 14, alignItems: "flex-start", minHeight: 56, paddingTop: 2 };

                                  if (item.status === "pass") {
                                    return (
                                      <div key={item.id} className="ir-row ir-row-pass" style={{ ...row, color: T.bodyStrong }}>
                                        <span style={{ color: T.good, flexShrink: 0 }}>✓</span>
                                        <span>{label}</span>
                                      </div>
                                    );
                                  }
                                  if (item.status === "fail") {
                                    return (
                                      <div
                                        key={item.id}
                                        className={`ir-row ir-row-fail${item.notes ? "" : " ir-no-notes"}`}
                                        style={{ ...row, flexDirection: "column", gap: 4 }}
                                      >
                                        <div style={{ display: "flex", gap: 10, color: T.bad, fontWeight: 600 }}>
                                          <span style={{ flexShrink: 0 }}>×</span>
                                          <span>{label}</span>
                                        </div>
                                        {item.notes && (
                                          <p style={{ fontSize: 13, lineHeight: 1.6, color: T.body, margin: "0 0 0 24px" }}>{item.notes}</p>
                                        )}
                                      </div>
                                    );
                                  }
                                  if (item.status === "na") {
                                    return (
                                      <div key={item.id} className="ir-row ir-row-na" style={{ ...row, color: T.muted }}>
                                        <span style={{ flexShrink: 0 }}>—</span>
                                        <span>{label}</span>
                                      </div>
                                    );
                                  }
                                  return null;
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── INSPECTOR'S CONCLUSION ───────────────────────────── */}
        <div className="ir-conclusion" style={S.conclusion}>
          <div className="ir-concl-text">
            <h2 style={{ ...S.h, fontSize: 26, color: T.darkText, margin: "0 0 12px" }}>Inspector's conclusion</h2>
            <p style={{ fontSize: 16, lineHeight: 1.6, color: T.darkBody, margin: 0, maxWidth: "56ch" }}>{r.verdict}</p>
          </div>
          <div className="ir-concl-sign">
            <div style={{ ...S.h, fontWeight: 600, fontStyle: "italic", fontSize: 20, color: T.darkText }}>
              {inspectorName || "Propspector"}
            </div>
            <div style={{ fontSize: 13, color: T.muted, marginTop: 6 }}>
              Propspector Inspector · {fmtDate(r.inspection_date || r.created_at)}
            </div>
          </div>
        </div>

        {/* ── ACTIONS ───────────────────────────────────────────── */}
        <section className="ir-sect no-print" style={S.sect}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 420, margin: "0 auto" }}>
            <button onClick={handleShare} className="ir-btn" style={S.btnPrimary}>
              <Share2 size={16} /> Share on WhatsApp
            </button>
            <button onClick={() => window.print()} className="ir-btn" style={S.btnGhost}>
              <Printer size={16} /> Save as PDF
            </button>
            <Link to="/inspect" className="ir-btn" style={S.linkAction}>
              Book another inspection <ArrowRight size={15} />
            </Link>
          </div>
        </section>

        {/* ── FOOTER ────────────────────────────────────────────── */}
        <footer className="ir-sect ir-footer" style={{ ...S.sect, paddingBottom: 48 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10 }}>
            <img src="/logo.png" alt="" style={{ width: 20, height: 20, borderRadius: 6 }} />
            <span style={{ ...S.h, fontSize: 14, color: T.ink }}>Propspector</span>
          </div>
          <p style={{ ...S.h, fontWeight: 400, fontSize: 15, lineHeight: 1.5, color: T.bodyStrong, margin: "0 0 18px" }}>
            Strike the truth before you strike the deal
          </p>
          <p style={{ fontSize: 12, lineHeight: 1.65, color: T.muted, margin: 0 }}>
            Visual and physical inspection only.{" "}
            <a href="https://propspector.in" style={{ color: T.accentInk, textDecoration: "none" }}>propspector.in</a>
          </p>
        </footer>
      </div>

      {/* Print-only footer, repeated on every printed page. ir-print-only
          is the same class the accordion's "N passed" line uses — proven
          display:none-on-screen/block-in-print toggle; ir-print-footer
          carries only the print-specific fixed positioning, nothing that
          could affect on-screen layout. */}
      <div className="ir-print-only ir-print-footer">propspector.in · Confidential · Ref {ref}</div>

      <style>{CSS}</style>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────
function Badge({ tone, fullWidth }) {
  return (
    <span
      className={`ir-badge${fullWidth ? " ir-badge--full" : ""}`}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: fullWidth ? "center" : "flex-start",
        gap: 6, flexShrink: 0, width: fullWidth ? "100%" : undefined, boxSizing: "border-box",
        padding: "6px 11px", borderRadius: 50, background: tone.bg,
        border: `1px solid ${tone.border}`, color: tone.fg,
        fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: 12, whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 50, background: tone.fg, flexShrink: 0 }} />
      {tone.label}
    </span>
  );
}

function StatCell({ label, value, valueColour }) {
  return (
    <div className="ir-stat">
      <div style={{ ...S.label, fontSize: 11, color: T.muted, letterSpacing: "0.08em", marginBottom: 8 }}>{label.toUpperCase()}</div>
      <div style={{ ...S.h, fontWeight: 600, fontSize: 20, color: valueColour || T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {value}
      </div>
    </div>
  );
}

function RecStep({ num, colour, title, desc, items, isLast }) {
  return (
    <div className="ir-rec-step" style={{ display: "flex", gap: 16 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <div
          style={{
            width: 28, height: 28, borderRadius: 50, flexShrink: 0, background: T.card,
            border: `1px solid ${colour}`, color: colour,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 13, zIndex: 1,
          }}
        >
          {num}
        </div>
        {!isLast && <div className="ir-rec-line" style={{ width: 2, flex: 1, background: T.hairline, marginTop: 4 }} />}
      </div>
      <div style={{ minWidth: 0, paddingBottom: isLast ? 0 : 28 }}>
        <h3 style={{ ...S.h, fontWeight: 600, fontSize: 18, lineHeight: 1.3, color: T.ink, margin: "0 0 8px" }}>{title}</h3>
        <p style={{ fontSize: 15, lineHeight: 1.65, color: T.body, margin: "0 0 12px", maxWidth: "64ch" }}>{desc}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(items || []).map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, fontSize: 14, color: T.bodyStrong }}>
              <span>{item.label}</span>
              {item.location && (
                <span className="ir-loc-chip" style={{ ...S.label, fontSize: 10, color: T.muted, background: T.bgPage, border: `1px solid ${T.hairline}`, borderRadius: 50, padding: "2px 9px" }}>
                  {item.location}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Shared shadcn Skeleton, driven by a token-coloured shimmer instead of pulse.
function SkelBar({ w = "100%", h = 12, radius = 8, dark = false }) {
  return (
    <Skeleton
      className="animate-none"
      style={{
        width: w, height: h, borderRadius: radius, display: "block",
        background: dark
          ? `linear-gradient(90deg, ${T.onDarkFaint} 0%, ${T.onDarkSoft} 50%, ${T.onDarkFaint} 100%)`
          : `linear-gradient(90deg, ${T.hairline} 0%, ${T.bgPage} 50%, ${T.hairline} 100%)`,
        backgroundSize: "200% 100%",
        animation: "ir-shimmer 1.4s ease-in-out infinite",
      }}
    />
  );
}

// ── Styles ───────────────────────────────────────────────────────
const LABEL = {
  fontFamily: "'Manrope', sans-serif", fontWeight: 600,
  textTransform: "uppercase", letterSpacing: "0.1em", fontVariantNumeric: "tabular-nums",
};

const S = {
  page: { minHeight: "100vh", background: T.bgPage, fontFamily: "'Manrope', sans-serif", color: T.ink },
  h: { fontFamily: "'Sora', sans-serif", fontWeight: 700, color: T.ink, margin: 0 },
  label: LABEL,

  hero: { position: "relative", background: T.dark, overflow: "hidden", width: "100%" },
  heroImg: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" },
  heroScrim: { position: "absolute", inset: 0, background: `linear-gradient(to top, ${T.dark} 0%, ${T.darkFade} 55%, ${T.darkClear} 100%)` },
  heroGlow: { position: "absolute", inset: 0, background: `radial-gradient(55% 55% at 90% 8%, ${T.accentGlow} 0%, ${T.accentClear} 70%)` },
  heroInner: { position: "relative", zIndex: 1, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", paddingTop: 24, paddingBottom: 72, boxSizing: "border-box" },

  refPill: { ...LABEL, fontSize: 10, color: T.darkText, border: `1px solid ${T.onDarkBorder}`, borderRadius: 50, padding: "8px 13px", whiteSpace: "nowrap" },
  exportPill: { display: "inline-flex", alignItems: "center", gap: 6, background: T.accent, color: T.dark, fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 12, border: "none", borderRadius: 50, padding: "11px 16px", cursor: "pointer", minHeight: 44 },

  statsStrip: { background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, boxShadow: `0 12px 32px ${T.shadowLg}`, marginTop: -48, position: "relative", zIndex: 2 },

  sect: {},
  cardBase: { background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 18 },

  findingText: { padding: 40, display: "flex", flexDirection: "column", minWidth: 0 },
  photoPill: { position: "absolute", top: 12, left: 12, zIndex: 2, background: T.glass, backdropFilter: "blur(6px)", color: T.ink, fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: 11, padding: "5px 10px", borderRadius: 6, whiteSpace: "nowrap" },
  photoMore: { position: "absolute", bottom: 12, right: 12, zIndex: 2, background: T.glass, backdropFilter: "blur(6px)", color: T.ink, fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: 11, padding: "7px 11px", borderRadius: 50, cursor: "pointer", whiteSpace: "nowrap" },

  conclusion: { background: T.dark, borderRadius: 20, padding: 40, display: "flex", flexDirection: "column", gap: 24 },

  btnPrimary: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, minHeight: 54, padding: "0 24px", background: T.accent, color: T.dark, fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 15, border: "none", borderRadius: 50, cursor: "pointer", textDecoration: "none" },
  btnGhost: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, minHeight: 50, padding: "0 24px", background: "transparent", color: T.ink, fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 15, border: `1px solid ${T.cardBorder}`, borderRadius: 50, cursor: "pointer", textDecoration: "none" },
  linkAction: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, minHeight: 50, color: T.accentInk, fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 14, textDecoration: "none" },
};

/*
 * PRINT DENSITY MATHS — target ~3 findings per A4 page (unchanged rules,
 * reapplied against the new markup)
 *
 * A4 is 297mm tall; with @page margin 10mm the printable band is 277mm.
 *
 * One finding card in print:
 *   image column is fixed at 56mm;
 *   text column is location/badge row ~5mm + title ~6mm + description
 *   clamped to 5 lines ~17mm + recommendation box ~13mm + footer row ~6mm +
 *   4mm padding top and bottom = ~51mm.
 * The 56mm plate still governs card height. Add the border and the 4mm
 * card margin-bottom → ~60mm of vertical rhythm per finding.
 *   277 / 60 = 4.6 → 4 cards fit a bare page.
 *
 * A continuation page carries no section heading, but break-inside: avoid
 * pushes any card that would straddle the page boundary wholly onto the
 * next page, which costs on average half a card of slack:
 *   4.6 - 0.5 ≈ 4 per page in practice, so still comfortably ≥3. ✔
 *
 * Page 1: hero (58mm cap) + stats strip (flat, ~20mm) + verdict section
 * (~45mm) + system summary (5-up, 2-3 rows ~24mm) + headings ≈ 160mm,
 * leaving ~117mm — one finding lands on page 1, the rest run ~3-4/page.
 */
const CSS = `
@keyframes ir-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.ir-btn, .ir-tile, .ir-cat-row, .ir-fig, .ir-fig img, .ir-photo-more, .ir-brand,
.ir-chevron, .ir-rec-step a {
  transition: all 0.2s ease;
}

.ir-btn:focus-visible, .ir-tile:focus-visible, a:focus-visible, button:focus-visible {
  outline: 2px solid ${T.accent};
  outline-offset: 2px;
}

.ir-container {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 24px;
  box-sizing: border-box;
}
@media (max-width: 479px) {
  .ir-container { padding: 0 16px; }
}

.ir-hero { height: 360px; }

.ir-stats-strip { display: grid; grid-template-columns: repeat(2, 1fr); }
.ir-stat { padding: 18px 16px; border-top: 1px solid ${T.hairline}; border-left: 1px solid ${T.hairline}; }
.ir-stat:nth-child(2n+1) { border-left: none; }
.ir-stat:nth-child(-n+2) { border-top: none; }
@media (min-width: 480px) {
  .ir-stats-strip { grid-template-columns: repeat(4, 1fr); }
  .ir-stat { border-top: none; border-left: 1px solid ${T.hairline}; }
  .ir-stat:first-child { border-left: none; }
}

.ir-sect { margin-top: 48px; }
.ir-stats-strip + .ir-sect { margin-top: 48px; }

.ir-tiles { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.ir-tile {
  background: ${T.card}; border: 1px solid ${T.cardBorder}; border-radius: 12px;
  padding: 16px; text-align: left; cursor: pointer; font: inherit;
  display: flex; flex-direction: column; gap: 10px;
}
.ir-tile-title { font-family: 'Manrope', sans-serif; font-weight: 600; font-size: 15px; color: ${T.ink}; }
.ir-tile:hover { transform: translateY(-3px); border-color: ${T.accent}; box-shadow: 0 8px 22px ${T.accentSoft}; }

.ir-finding {
  background: ${T.card}; border: 1px solid ${T.cardBorder}; border-radius: 16px;
  box-shadow: 0 12px 32px ${T.shadowLg}; overflow: hidden;
  display: grid; grid-template-columns: 1fr; gap: 0;
}
.ir-findings-list { display: flex; flex-direction: column; gap: 20px; }
.ir-fig { position: relative; aspect-ratio: 4 / 3; overflow: hidden; order: 0; max-width: 100%; cursor: pointer; }
.ir-fig img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ir-fig:hover img { transform: scale(1.02); }

.ir-btn:hover, .ir-brand:hover { opacity: 0.86; }
.ir-cat-row:hover { opacity: 0.76; }
.ir-rec-step a:hover { opacity: 0.8; }

.ir-checklist-card { overflow: hidden; }

.ir-conclusion { margin-top: 56px; }

.ir-print-only, .ir-print-footer { display: none; }

@media (min-width: 480px) {
  .ir-tiles { grid-template-columns: repeat(3, 1fr); }
}

@media (min-width: 768px) {
  .ir-sect { margin-top: 72px; }
  .ir-stats-strip + .ir-sect { margin-top: 72px; }
  .ir-hero { height: 460px; }
  .ir-hero-inner { padding-bottom: 88px; }

  .ir-tiles { grid-template-columns: repeat(4, 1fr); }

  .ir-finding.ir-has-photo { grid-template-columns: 1fr 1fr; }
  .ir-finding.ir-has-photo .ir-fig { aspect-ratio: auto; min-height: 380px; height: 100%; }
  .ir-finding--flip .ir-fig { order: 2; }
  .ir-ftext { padding: 40px; }

  .ir-conclusion { flex-direction: row; align-items: center; justify-content: space-between; gap: 40px; }
  .ir-concl-sign { text-align: right; flex-shrink: 0; }
}

/* @page only ever applies when paginating (print/PDF), but it must be a
   top-level rule — nested inside @media print it's invalid per the CSS
   Paged Media spec and browsers silently drop it, so A4 sizing and margins
   never take effect. Keep it out here. */
@page {
  size: A4;
  margin: 10mm;
}

@media print {
  html, body, .ir-page {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    background: ${T.card} !important;
  }

  .no-print { display: none !important; }

  /* Base 10pt body; headings scaled to about 70% */
  .ir-sect { font-size: 10pt !important; margin-top: 5mm !important; }
  .ir-sect-h { font-size: 15px !important; margin-bottom: 2mm !important; break-after: avoid !important; page-break-after: avoid !important; }
  .ir-hero-title { font-size: 26px !important; }
  .ir-ftitle { font-size: 15px !important; }
  .ir-verdict-line { font-size: 18px !important; }
  .ir-summary { font-size: 9pt !important; }

  /* HERO — capped at 58mm. No cover photo → white + ink, to save ink. */
  .ir-hero { height: 58mm !important; max-height: 58mm !important; }
  .ir-hero-inner { padding: 4mm 0 !important; }
  .ir-hero-glow { display: none !important; }
  .ir-hero--nophoto { background: ${T.card} !important; border-bottom: 3px solid ${T.accent} !important; }
  .ir-hero--nophoto .ir-brand-name,
  .ir-hero--nophoto .ir-hero-title { color: ${T.ink} !important; }
  .ir-hero--nophoto .ir-hero-eyebrow { color: ${T.accentInk} !important; }
  .ir-hero--nophoto .ir-ref { color: ${T.ink} !important; border-color: ${T.hairline} !important; }

  /* STATS STRIP — no shadow, no negative margin */
  .ir-stats-strip { box-shadow: none !important; margin-top: 3mm !important; border-radius: 0 !important; grid-template-columns: repeat(4, 1fr) !important; }
  .ir-stat { padding: 2.5mm 3mm !important; border-top: none !important; }
  .ir-stat:first-child { border-left: none !important; }

  .ir-sect { margin-top: 4mm !important; }

  /* SYSTEM SUMMARY — tight 5-up so it comfortably stays on page 1 */
  .ir-tiles { grid-template-columns: repeat(5, 1fr) !important; gap: 1.5mm !important; }
  .ir-tile { padding: 2mm !important; gap: 1mm !important; }
  .ir-tile-title { font-size: 8.5pt !important; line-height: 1.15 !important; }
  .ir-tile .ir-badge { font-size: 7.5pt !important; padding: 0.6mm 1.6mm !important; gap: 1mm !important; }
  .ir-tile .ir-badge > span:first-child { width: 3px !important; height: 3px !important; }

  /* Chrome's print pagination does not reliably fragment children of a flex
     container — cards get pushed whole to the next page even when they'd
     fit the remaining space, wasting half a page at a time. Block layout
     paginates predictably; margin-bottom (below) replaces the flex gap. */
  .ir-findings-list { display: block !important; }

  /* FINDINGS — two columns for photo cards (40/60, alternation kept,
     56mm plate); text-only cards have no .ir-fig at all, so forcing the
     same 40/60 template on them squeezed their one child into the first
     40% track — wrapping "LOCATION · CATEGORY" onto an extra line and
     overflowing the 56mm budget, clipping the card instead of paginating
     it cleanly. Scope the two-column template to .ir-has-photo only;
     text-only cards stay the single column they already are on screen. */
  .ir-finding {
    margin-bottom: 4mm !important;
    box-shadow: none !important;
    opacity: 1 !important;
    transform: none !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
  .ir-finding.ir-has-photo { grid-template-columns: 40% 60% !important; }
  .ir-finding.ir-has-photo.ir-finding--flip { grid-template-columns: 60% 40% !important; }
  .ir-finding--flip .ir-fig { order: 2 !important; }
  .ir-fig {
    aspect-ratio: auto !important;
    min-height: 0 !important;
    height: 56mm !important;
    max-height: 56mm !important;
  }
  /* CSS Grid stretches the row to whichever column is tallest. .ir-fig's
     own height:56mm only constrains the image itself — without also
     capping the text column, a grid row with rich-enough text (now
     bigger fonts + the restored footer row) sizes to the TEXT's natural
     height instead of the image, silently inflating every card past
     56mm and cutting density from ~3/page to ~2. Capping + clipping
     ir-ftext at the same 56mm keeps the row (and so the card) pinned —
     but only for photo cards; a text-only card has no image to match
     and should just be as tall as its own content needs. */
  .ir-ftext { padding: 4mm !important; }
  .ir-has-photo .ir-ftext { max-height: 56mm !important; overflow: hidden !important; }
  .ir-desc {
    display: -webkit-box !important;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 9pt !important;
    line-height: 1.5 !important;
    margin-bottom: 2mm !important;
  }
  .ir-rec { padding: 2mm !important; }
  .ir-rec p { font-size: 8.5pt !important; line-height: 1.4 !important; }
  .ir-ffooter { padding-top: 2.5mm !important; }
  .ir-photo-more { display: none !important; }

  /* WHAT TO DO NEXT — timeline line prints as-is */
  .ir-rec-step { break-inside: avoid !important; page-break-inside: avoid !important; }
  .ir-callout .no-print { display: none !important; }

  /* ACCORDION — every category expanded, but failed-with-notes only */
  .ir-cat-panel { height: auto !important; overflow: visible !important; }
  .ir-cat-row { min-height: 0 !important; }
  .ir-chevron { display: none !important; }
  .ir-cat { break-inside: avoid !important; page-break-inside: avoid !important; }
  .ir-row-pass, .ir-row-na, .ir-no-notes, .ir-inst-loc { display: none !important; }
  .ir-row { min-height: 0 !important; margin-bottom: 1.5mm !important; }
  .ir-print-only { display: block !important; }

  /* CONCLUSION — white box with an ink border instead of the dark fill */
  .ir-conclusion {
    background: ${T.card} !important;
    border: 1px solid ${T.ink} !important;
    padding: 4mm !important;
    flex-direction: row !important;
    justify-content: space-between !important;
    align-items: center !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
  .ir-conclusion h2, .ir-conclusion div { color: ${T.ink} !important; }
  .ir-conclusion p { color: ${T.bodyStrong} !important; }
  .ir-concl-sign { text-align: right !important; }

  /* Fixed footer on every printed page */
  .ir-print-footer {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    text-align: center;
    font-family: 'Manrope', sans-serif;
    font-size: 8pt;
    color: ${T.muted};
  }
}
`;
